'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { ParameterCod } from '@/constants/parameters';
import { cx } from '@/utils';

import { useLayoutConfigContext } from '@/components/common/Layout/LayoutConfig/LayoutConfigContext';

/** Clave en localStorage para el consentimiento. Solo afecta a analytics/GTM; las cookies de sesión (Auth, Middleware) no se tocan. */
const COOKIE_CONSENT_KEY = 'cookie-consent';
const COOKIE_CONSENT_ACCEPTED = 'true';

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

function CookieIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 256 256"
      fill="currentColor"
      className={cx('size-10 shrink-0 md:size-12', className)}
      aria-hidden
    >
      <path d="M223.45117,118.0293a11.95148,11.95148,0,0,0-10.09521-2.31348,27.993,27.993,0,0,1-34.15381-27,11.97247,11.97247,0,0,0-11.91748-11.918,27.99308,27.99308,0,0,1-27.00049-34.1543,12.01319,12.01319,0,0,0-11.68359-14.63769c-.2085-.001-.41309-.00195-.62207-.00195a99.99974,99.99974,0,1,0,100.01611,99.38281A11.99348,11.99348,0,0,0,223.45117,118.0293Zm-30.45605,75.083c-35.37354,35.3125-93.29492,35.69922-129.11475.86329a91.99664,91.99664,0,0,1,64.1001-157.97168c.19043,0,.38183.001.57275.002a4.04183,4.04183,0,0,1,3.15772,1.52637,3.92615,3.92615,0,0,1,.76758,3.35742,35.99441,35.99441,0,0,0,34.71923,43.9082,4.0235,4.0235,0,0,1,4.00489,4.00391,35.99387,35.99387,0,0,0,43.90625,34.71972,3.94377,3.94377,0,0,1,3.36279.76954,4.02736,4.02736,0,0,1,1.523,3.14453A91.33176,91.33176,0,0,1,192.99512,193.1123Zm-31.33838-26.769a7.99984,7.99984,0,1,1-11.31348,0A7.99959,7.99959,0,0,1,161.65674,166.34326Zm-64-8a7.99984,7.99984,0,1,1-11.31348,0A7.99959,7.99959,0,0,1,97.65674,158.34326Zm-8-44.68652a7.99984,7.99984,0,1,1,0-11.31348A7.99915,7.99915,0,0,1,89.65674,113.65674Zm52,4.68652a7.99984,7.99984,0,1,1-11.31348,0A7.99959,7.99959,0,0,1,141.65674,118.34326Z" />
    </svg>
  );
}

export function CookieConsentModal() {
  const [hasConsent, setHasConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const {
    layoutConfig: { parametersDefault },
  } = useLayoutConfigContext();

  const privacyUrl =
    parametersDefault?.find((x) => x.code === ParameterCod.PersonalDataProcessingPolicy)
      ?.character ?? '#';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === COOKIE_CONSENT_ACCEPTED) {
      setHasConsent(true);
    } else {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, COOKIE_CONSENT_ACCEPTED);
    setHasConsent(true);
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'false');
    setHasConsent(false);
    setShowBanner(false);
  };

  return (
    <>
      {/* Google Analytics solo se carga con consentimiento; cookies de sesión (Auth/Middleware) no se ven afectadas */}
      {hasConsent && GA_MEASUREMENT_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
            strategy="afterInteractive"
          />
          <Script id="google-analytics" strategy="afterInteractive">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_MEASUREMENT_ID}', {
                page_path: window.location.pathname,
              });
            `}
          </Script>
        </>
      )}
      {showBanner && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-desc"
          className={cx(
            'fixed bottom-6 left-1/2 z-modal w-[calc(100%-2rem)] max-w-6xl -translate-x-1/2',
            'animate-slide-in-bottom',
            'rounded-xl bg-white font-workSans',
            'shadow-[0_8px_30px_rgba(0,0,0,0.12)]'
          )}
        >
          <div
            className={cx(
              'flex w-full flex-wrap items-center gap-4 p-4',
              'md:flex-nowrap md:gap-4 md:p-5'
            )}
          >
            <div className="flex shrink-0 items-center justify-center rounded-full bg-primary/10 p-2">
              <CookieIcon className="text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h2
                id="cookie-consent-title"
                className="text-base font-semibold text-primary md:text-lg"
              >
                Uso de cookies
              </h2>
              <p
                id="cookie-consent-desc"
                className="mt-1 text-sm leading-relaxed text-gray-600 md:text-base"
              >
                Utilizamos cookies propias y de terceros para mejorar la experiencia de navegación,
                analizar el uso del sitio y personalizar el contenido. Al continuar, aceptas nuestra{' '}
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-link text-marine hover:underline"
                >
                  política de cookies
                </a>
                .
              </p>
            </div>
            <div className="flex w-full shrink-0 justify-end gap-3 md:w-auto">
              <button
                type="button"
                onClick={handleReject}
                className={cx(
                  'rounded-lg border-2 border-primary bg-transparent px-4 py-2.5',
                  'font-semibold text-primary',
                  'transition-colors hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                )}
              >
                Rechazar
              </button>
              <button
                type="button"
                onClick={handleAccept}
                className={cx(
                  'rounded-lg border-2 border-primary bg-primary px-5 py-2.5',
                  'font-semibold text-white',
                  'transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                )}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieConsentModal;
